import { faChild, faUser, faUserPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, ListItemIcon, ListItemText, Menu, MenuItem, MenuList } from '@vapor/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiCIcon } from 'src/app/components/icons/icon'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { ReservationF, ReservedResourceF } from '../../../../types/reservation-detail.types'
import { ReservedResourcePhase, State } from 'src/api/api.generated'
import { splitGuestOccupancy } from '../../../../api/reserved-resource-utils'

export type AddGuestButtonProps = {
  readOnly: boolean
  reservation: ReservationF
  reservedResourceId: string
  addGuest: (childId: string | null, isReplaceOfCheckoutGuest?: boolean) => Promise<void>
}

export default function AddGuestButton({ readOnly, reservation, reservedResourceId, addGuest }: AddGuestButtonProps) {
  const { t } = useTranslation()
  const localizeString = useLocalizeString()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = useMemo(() => anchorEl !== null, [anchorEl])
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const options: GuestOption[] = useMemo(() => {
    const reservedResource = reservation.reservedResources?.find((rr) => rr.id === reservedResourceId)
    if (!reservedResource) return []

    const adultOption: GuestOption = {
      childId: null,
      description: t('pms.booking.reservation.guest.adult'),
      available: canAddGuest(true, reservedResource),
      canReplaceCheckoutGuest: isGuestReplacement(true, reservedResource),
    }

    const childOptions: GuestOption[] =
      reservedResource.rate.childRule.childDiscount
        ?.filter((child) => child.child.auditable.state === State.ACTIVE)
        ?.map((c) => ({
          childId: c.child.id,
          description: localizeString(c.child.taxonomy.description),
          available: c.child.noOccupancy || canAddGuest(false, reservedResource),
          canReplaceCheckoutGuest: isGuestReplacement(false, reservedResource),
        })) ?? []

    return [adultOption, ...childOptions]
  }, [reservation])

  return (
    <>
      <Button variant="outlined" onClick={handleClick} disabled={readOnly} size="small">
        <FontAwesomeIcon icon={faUserPlus} style={{ marginRight: 2 }} />
        <span style={{ marginLeft: 2 }}>{t('pms.booking.reservation.guest.action.add_guest')}</span>
      </Button>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuList>
          {options.map((o, i) => (
            <MenuItem
              key={i}
              onClick={() => {
                handleClose()
                addGuest(o.childId, o.canReplaceCheckoutGuest)
              }}
              disabled={!o.available && !o.canReplaceCheckoutGuest}
            >
              <ListItemIcon>
                <HiCIcon>
                  <FontAwesomeIcon icon={o.childId === null ? faUser : faChild} size="sm" />
                </HiCIcon>
              </ListItemIcon>
              <ListItemText>{o.description}</ListItemText>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </>
  )
}

type GuestOption = {
  childId: string | null
  available: boolean
  description: string
  canReplaceCheckoutGuest: boolean
}

function canAddGuest(adult: boolean, reservedResource: ReservedResourceF): boolean {
  const resourceType = reservedResource.currentResourceOccupancy.reservedResourceType
  const adults = pos(reservedResource.adults)
  const occupingChldren = reservedResource.childOccupancies?.filter((c) => !c.child.noOccupancy)?.reduce((p, v) => p + v.quantity, 0) ?? 0
  const children = pos(occupingChldren)
  const persons = pos(resourceType.persons)
  const additionalPersons = pos(resourceType.additionalPersons)
  const maxAdults = pos(resourceType.maxAdults ?? persons + additionalPersons)

  if (adult && adults + 1 > maxAdults) return false
  return pos(persons + additionalPersons - adults - children) > 0
}

function pos(n: number): number {
  return Math.max(0, n)
}

/**
 * Check if the guest can replace another one who has checked out
 * @param adult
 * @param reservedResource
 * @returns
 */
function isGuestReplacement(adult: boolean, reservedResource: ReservedResourceF): boolean {
  const splittedGo = splitGuestOccupancy(reservedResource)

  if (!reservedResource.guestOccupancies || reservedResource.guestOccupancies.length === 0) return false

  if (adult) {
    return splittedGo.notOutGuests.adults < reservedResource.adults
  } else {
    return splittedGo.notOutGuests.children < reservedResource.children
  }
}
